<template>
	<div
		ref="imageCarousel"
		class="image-carousel"
		data-cy="image-carousel"
		tabindex="0"
	>
		<div
			class="image-carousel__container"
			:style="{ height: `${thumbnailHeight}px` }"
		>
			<transition-group
				:name="`carouslide-${direction}`"
				tag="div"
				class="image-carousel__thumbnails-container"
			>
				<template v-for="(image, index) in images">
					<img
						v-if="index === currentIndex"
						ref="thumbnail"
						:key="index"
						:src="src(image)"
						:alt="alt(image)"
						class="image-carousel__thumbnail"
					>
				</template>
			</transition-group>

			<div
				class="image-carousel__navigation"
				:class="{ 'image-carousel__navigation--hasFocus': hasFocus }"
			>
				<helio-icon
					ref="previous"
					class="image-carousel__control image-carousel__control--previous"
					token="angle-left"
					weight="light"
					aria-hidden="true"
					@keyup.enter.native="decrementIndex"
					@click="decrementIndex"
				/>

				<helio-icon
					ref="next"
					class="image-carousel__control image-carousel__control--next"
					token="angle-right"
					weight="light"
					aria-hidden="true"
					@keyup.enter.native="incrementIndex"
					@click="incrementIndex"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import Icon from '../../icon/Icon.vue';

export default {
	name: 'ImageCarousel',

	components: {
		'helio-icon': Icon,
	},

	props: {
		images: {
			type: Array,
			required: true,
			description: 'Series of images for the image carousel of a card thumbnail',
		},
	},

	data() {
		return {
			currentIndex: 0,
			thumbnailHeight: 0,
			direction: 'left',
			hasFocus: false,
		};
	},

	mounted() {
		const { imageCarousel } = this.$refs;

		if (imageCarousel) {
			imageCarousel.addEventListener('keydown', this.checkForKeyboardNavigation);
		}

		window.addEventListener('resize', this.onResize);

		setTimeout(() => {
			this.onResize();
		}, 200);
	},

	beforeDestroy() {
		const { imageCarousel } = this.$refs;

		if (imageCarousel) {
			imageCarousel.removeEventListener('keydown', this.checkForKeyboardNavigation);
		}

		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		src(image) {
			if (typeof image === 'object') {
				return image.link;
			}

			return image;
		},

		alt(image) {
			if (typeof image === 'object') {
				return image.alt;
			}

			return '';
		},

		onResize() {
			const { thumbnail } = this.$refs;

			if (thumbnail && thumbnail[0]) {
				setTimeout(() => {
					this.thumbnailHeight = thumbnail[0].offsetHeight;
				}, 500);
			}
		},

		decrementIndex() {
			this.direction = 'left';

			if (this.currentIndex - 1 >= 0) {
				this.currentIndex -= 1;
			} else {
				this.currentIndex = this.images.length - 1;
			}
		},

		incrementIndex() {
			this.direction = 'right';

			if ((this.currentIndex + 1) <= (this.images.length - 1)) {
				this.currentIndex += 1;
			} else {
				this.currentIndex = 0;
			}
		},

		isHidden(index) {
			return index < this.currentIndex || index > this.currentIndex;
		},

		checkForKeyboardNavigation(event) {
			const { keyCode } = event;

			this.hasFocus = true;

			// Left arrow
			if (keyCode === 37) {
				this.decrementIndex();
			}
			// Right arrow
			if (keyCode === 39) {
				this.incrementIndex();
			}
		},
	},
};
</script>

<style lang="scss" src="./image-carousel.scss" />
